'use client'

// Follow a Twetch user from the connected BTC wallet: one signature, free,
// fee paid by Twetch's paymaster. Hidden for the viewer's own account.

import React from 'react'
import useSwr from 'swr'
import { Check, UserPlus } from 'lucide-react'
import { cn } from '@/lib/utils'
import event from '@/utils/event'
import { twetchJson } from '../lib/twetch-api'
import useTwetchActions from '../lib/use-twetch-actions'
import useLoggedIn from '@/utils/hooks/useLoggedIn'
import featureFlags from '@/utils/feature-flags'
import { SOCIAL_PROFILES_FLAG } from './user-hover-card'

export default function FollowButton({
	userId,
	className
}: {
	userId: number
	className?: string
}) {
	const actions = useTwetchActions()
	const loggedIn = !!useLoggedIn()
	const viewerId = actions.viewerId
	const { data } = useSwr<{ youFollow?: boolean }>(
		viewerId !== null && viewerId !== userId ? `alkanes-twetch-rel-${viewerId}-${userId}` : null,
		() => twetchJson(`/v1/users/${userId}/relationship?userId=${viewerId}`),
		{ revalidateOnFocus: false }
	)
	if (!featureFlags(SOCIAL_PROFILES_FLAG)) {
		return null
	}
	if (viewerId === userId) {
		return null
	}
	const following = actions.isFollowing(userId, data?.youFollow)
	const busy = actions.busyKey === `follow-${userId}`
	const error = actions.error?.key === `follow-${userId}` ? actions.error.message : null

	return (
		<span className={cn('inline-flex flex-col', className)}>
			<button
				type="button"
				data-testid="alkanes-follow"
				aria-pressed={following}
				disabled={busy || following}
				onClick={() => {
					if (!loggedIn) {
						event.emit('command', { id: 'connect-wallet' })
						return
					}
					void actions.follow(userId)
				}}
				className={cn(
					'inline-flex h-9 items-center justify-center gap-1.5 rounded-xl px-3.5 text-xs font-bold transition-colors disabled:cursor-default',
					following
						? 'bg-secondary text-muted-foreground'
						: 'bg-primary text-primary-foreground hover:bg-primary/90',
					busy && 'animate-pulse'
				)}
			>
				{following ? (
					<Check className="size-3.5" aria-hidden="true" />
				) : (
					<UserPlus className="size-3.5" aria-hidden="true" />
				)}
				{busy ? 'Check wallet…' : following ? 'Following' : 'Follow'}
			</button>
			{error && (
				<span role="alert" className="mt-1 max-w-[24ch] text-xxs leading-snug text-destructive-ink">
					{error}
				</span>
			)}
		</span>
	)
}
